import React, { useMemo } from 'react';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@carbon/react';
import { ServiceTabId, settingsServiceMap } from '../settingsTypes';
import OneGUISettings from '../services/OneGUISettings';
import WatchfolderSettings from '../services/WatchfolderSettings';
import FileQueuerSettings from '../services/FileQueuerSettings';
import IngestSettings from '../services/IngestSettings';
import RecorderSettings from '../services/RecorderSettings';
import ComplianceRecordSettings from '../services/ComplianceRecordSettings';
import { isServiceEnabledByBuild } from '../../../config/build';

interface ServicesTabProps {
  selectedServiceId: ServiceTabId;
  onServiceChange: (serviceId: ServiceTabId) => void;
}

const servicePanels: { id: ServiceTabId; label: string; Component: React.ComponentType }[] = [
  { id: 'onegui', label: 'OneGUI', Component: OneGUISettings },
  { id: 'watchfolder', label: 'Watchfolder', Component: WatchfolderSettings },
  { id: 'fileQueuer', label: 'File Queuer', Component: FileQueuerSettings },
  { id: 'ingest', label: 'Ingest', Component: IngestSettings },
  { id: 'recorder', label: 'Recorder', Component: RecorderSettings },
  { id: 'complianceRecord', label: 'Encoder', Component: ComplianceRecordSettings },
];

const ServicesTab: React.FC<ServicesTabProps> = ({ selectedServiceId, onServiceChange }) => {
  const visiblePanels = useMemo(
    () => servicePanels.filter((panel) => isServiceEnabledByBuild(settingsServiceMap[panel.id])),
    []
  );

  const selectedIndex = useMemo(
    () => Math.max(visiblePanels.findIndex(({ id }) => id === selectedServiceId), 0),
    [selectedServiceId, visiblePanels]
  );

  return (
    <div className="settings-page__panel" style={{ minWidth: 800}}>
      <div className="settings-page__panel-header">
        <div>
          <h3>Services settings</h3>
          <p>Manage internal services and their configuration.</p>
        </div>
      </div>

      <Tabs
        selectedIndex={selectedIndex}
        onChange={({ selectedIndex }) => {
          const nextTab = visiblePanels[selectedIndex]?.id ?? visiblePanels[0]?.id;
          if (nextTab !== selectedServiceId) {
            onServiceChange(nextTab);
          }
        }}
      >
        <TabList aria-label="Services" contained>
          {visiblePanels.map(tab => (
            <Tab key={tab.id}>{tab.label}</Tab>
          ))}
        </TabList>
        <TabPanels>
          {visiblePanels.map(({ id, Component }) => (
            <TabPanel key={id}>
              <Component />
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </div>
  );
};

export default ServicesTab;
